import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { UiTextsContext, UserDataContext } from "./Contexts";
import { getMessagesService } from "../../infrastructure/services/NotificationsService";
import { hasUnreadMessages } from "../components/utils/messageUnread";

const MessagesFeedContext = createContext(null);

const getMessageContactId = (message = {}) =>
  message.idUsuarioOrigen ??
  message.idAmigo ??
  message.idPersona ??
  message.idUsuario ??
  message.id ??
  null;

const normalizeIdentityText = (value) =>
  String(value ?? "")
    .trim()
    .replace(/^@/, "")
    .toLowerCase();

const isMessageFromActiveConversation = (message, activeConversation) => {
  if (!activeConversation) return false;

  const activeIds = new Set(
    [activeConversation.contactId, activeConversation.chatId]
      .filter(Boolean)
      .map(String),
  );
  const messageIds = [
    message?.idUsuarioOrigen,
    message?.idAmigo,
    message?.idPersona,
    message?.idUsuario,
    message?.id,
    message?.idChat,
    message?.IdChat,
  ]
    .filter(Boolean)
    .map(String);

  if (messageIds.some((id) => activeIds.has(id))) return true;

  const activeNickname = normalizeIdentityText(activeConversation.nickName);
  const messageNickname = normalizeIdentityText(message?.nickName);
  if (activeNickname && activeNickname === messageNickname) return true;

  const activeName = normalizeIdentityText(activeConversation.name);
  const messageName = normalizeIdentityText(
    message?.nombre ?? message?.name ?? message?.nombres,
  );
  return Boolean(activeName && activeName === messageName);
};

const getMessageTimestamp = (message = {}) => {
  const value = new Date(message.fechaEnvio ?? message.timestamp ?? 0).getTime();
  return Number.isFinite(value) ? value : 0;
};

const consolidateLatestMessages = (messages = [], activeConversation = null) => {
  const latestByContact = new Map();

  (Array.isArray(messages) ? messages : []).forEach((message) => {
    const contactId = getMessageContactId(message);
    const key = contactId
      ? String(contactId)
      : `chat:${message?.idChat ?? message?.idMensaje ?? "unknown"}`;
    const current = latestByContact.get(key);

    if (!current || getMessageTimestamp(message) >= getMessageTimestamp(current)) {
      latestByContact.set(key, message);
    }
  });

  return Array.from(latestByContact.values())
    .map((message) => {
      const isActive = isMessageFromActiveConversation(
        message,
        activeConversation,
      );

      return isActive
        ? {
            ...message,
            mensajesNoLeidos: 0,
            estatusMensaje: "1",
          }
        : message;
    })
    .sort((a, b) => getMessageTimestamp(b) - getMessageTimestamp(a));
};

export const useMessagesFeedState = () => {
  const { userData } = useContext(UserDataContext);
  const { texts } = useContext(UiTextsContext);
  const [messagesList, setMessagesList] = useState([]);
  const [showMessageDot, setShowMessageDot] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const previousMessagesSignatureRef = useRef("");
  const rawMessagesRef = useRef([]);
  const activeConversationRef = useRef(null);
  const unreadContactIdsRef = useRef(new Set());
  const readContactIdsRef = useRef(new Set());

  const buildMessagesSignature = useCallback(
    (messages = []) =>
      JSON.stringify(
        (Array.isArray(messages) ? messages : []).map((message) => ({
          idChat: message?.idChat ?? "",
          idUsuarioOrigen: message?.idUsuarioOrigen ?? "",
          estatusMensaje: String(message?.estatusMensaje ?? ""),
          fechaEnvio: message?.fechaEnvio ?? "",
          mensaje: message?.mensaje ?? "",
          mensajesNoLeidos: message?.mensajesNoLeidos ?? 0,
        })),
      ),
    [],
  );

  const applyUnreadOverrides = useCallback((messages = []) => {
    return messages.map((message) => {
      const contactId = getMessageContactId(message);

      if (!contactId) {
        return message;
      }

      const contactKey = String(contactId);

      if (readContactIdsRef.current.has(contactKey)) {
        if (
          String(message?.estatusMensaje ?? "") === "1" ||
          Number(message?.mensajesNoLeidos ?? 0) === 0
        ) {
          readContactIdsRef.current.delete(contactKey);
        }

        return {
          ...message,
          mensajesNoLeidos: 0,
          estatusMensaje: "1",
          status: "read",
        };
      }

      if (!unreadContactIdsRef.current.has(contactKey)) {
        return message;
      }

      return {
        ...message,
        mensajesNoLeidos: Math.max(1, Number(message?.mensajesNoLeidos ?? 0)),
        estatusMensaje: "0",
        status: "unread",
      };
    });
  }, []);

  const normalizeFeedMessages = useCallback(
    (messages = [], { updateRaw = true } = {}) => {
      const source = Array.isArray(messages) ? messages : [];

      if (updateRaw) {
        rawMessagesRef.current = source;
      }

      const messagesWithUnread = applyUnreadOverrides(source);

      return consolidateLatestMessages(
        messagesWithUnread,
        activeConversationRef.current,
      );
    },
    [applyUnreadOverrides],
  );

  const transformMessages = useCallback(
    (messages = []) => {
      const normalizedMessages = normalizeFeedMessages(messages, {
        updateRaw: true,
      });
      const unreadCount = normalizedMessages.filter(hasUnreadMessages).length;

      setMessagesList(normalizedMessages);
      setUnreadMessagesCount(unreadCount);
      setShowMessageDot(unreadCount > 0);
    },
    [normalizeFeedMessages],
  );

  const markContactAsUnreadInFeed = useCallback(
    (contactId) => {
      if (contactId) {
        unreadContactIdsRef.current.add(String(contactId));
      }

      transformMessages(rawMessagesRef.current);
    },
    [transformMessages],
  );

  const markContactAsReadInFeed = useCallback(
    (contactId) => {
      if (!contactId) {
        return;
      }

      const contactKey = String(contactId);
      unreadContactIdsRef.current.delete(contactKey);
      readContactIdsRef.current.add(contactKey);

      const updatedMessages = rawMessagesRef.current.map((message) => {
        const messageContactId = getMessageContactId(message);

        if (String(messageContactId ?? "") !== String(contactId)) {
          return message;
        }

        return {
          ...message,
          mensajesNoLeidos: 0,
          estatusMensaje: "1",
          status: "read",
        };
      });

      transformMessages(updatedMessages);
    },
    [transformMessages],
  );

  useEffect(() => {
    const handleActiveConversationChanged = (event) => {
      const contactId = event?.detail?.contactId;

      activeConversationRef.current = event?.detail ?? null;

      if (contactId) {
        const contactKey = String(contactId);
        unreadContactIdsRef.current.delete(contactKey);
        readContactIdsRef.current.add(contactKey);
      }

      transformMessages(rawMessagesRef.current);
    };

    window.addEventListener(
      "chat:active-conversation-changed",
      handleActiveConversationChanged,
    );

    return () => {
      window.removeEventListener(
        "chat:active-conversation-changed",
        handleActiveConversationChanged,
      );
    };
  }, [transformMessages]);

  useEffect(() => {
    const handleMarkContactRead = (event) => {
      markContactAsReadInFeed(event?.detail?.contactId ?? null);
    };

    window.addEventListener("messages:mark-contact-read", handleMarkContactRead);

    return () => {
      window.removeEventListener(
        "messages:mark-contact-read",
        handleMarkContactRead,
      );
    };
  }, [markContactAsReadInFeed]);

  useEffect(() => {
    const getRealtimeActorId = (payload = {}) =>
      payload.senderId ??
      payload.idUsuario ??
      payload.idUsuarioOrigen ??
      payload.fromUserId ??
      null;

    const handleRealtimeActivity = (event) => {
      const activeConversation = activeConversationRef.current;
      const payload = event?.detail?.args?.[0] ?? {};
      const actorId = getRealtimeActorId(payload);

      if (
        activeConversation?.contactId &&
        actorId &&
        String(activeConversation.contactId) === String(actorId)
      ) {
        return;
      }

      markContactAsUnreadInFeed(actorId);
    };

    window.addEventListener("chat:realtime-message", handleRealtimeActivity);
    window.addEventListener("chat:realtime-reaction", handleRealtimeActivity);

    return () => {
      window.removeEventListener("chat:realtime-message", handleRealtimeActivity);
      window.removeEventListener("chat:realtime-reaction", handleRealtimeActivity);
    };
  }, [markContactAsUnreadInFeed]);

  const syncMessages = useCallback(
    (messages = [], options = {}) => {
      const { notifySidebar = false, source = "unknown" } = options;
      const nextSignature = buildMessagesSignature(messages);
      const previousSignature = previousMessagesSignatureRef.current;
      const hasMeaningfulChange = previousSignature !== nextSignature;

      transformMessages(messages);
      previousMessagesSignatureRef.current = nextSignature;

      if (notifySidebar && hasMeaningfulChange) {
        window.dispatchEvent(
          new CustomEvent("messages:list-updated", {
            detail: {
              source,
              total: Array.isArray(messages) ? messages.length : 0,
            },
          }),
        );
      }

      return messages;
    },
    [buildMessagesSignature, transformMessages],
  );

  const refreshMessages = useCallback(
    async (options = {}) => {
      if (!userData?.idUsuario) {
        transformMessages([]);
        return [];
      }

      try {
        const messages = await getMessagesService(userData.idUsuario, {
          mostrarArchivados: false,
        });
        return syncMessages(messages, options);
      } catch (error) {
        console.error("Failed get messages", error);
        console.error(
          texts?.ER0072 ||
            "Error al obtener los mensajes, por favor intentelo de nuevo mas tarde.",
        );
        return [];
      }
    },
    [syncMessages, texts?.ER0072, transformMessages, userData?.idUsuario],
  );

  const resetMessagesFeed = useCallback(() => {
    unreadContactIdsRef.current.clear();
    readContactIdsRef.current.clear();
    transformMessages([]);
  }, [transformMessages]);

  return {
    messagesList,
    showMessageDot,
    unreadMessagesCount,
    refreshMessages,
    resetMessagesFeed,
  };
};

export const MessagesFeedProvider = ({ value, children }) => {
  return (
    <MessagesFeedContext.Provider value={value}>
      {children}
    </MessagesFeedContext.Provider>
  );
};

export const useMessagesFeed = () => {
  const context = useContext(MessagesFeedContext);

  if (!context) {
    throw new Error(
      "useMessagesFeed must be used within a MessagesFeedProvider",
    );
  }

  return context;
};
